/**
 * Which ear, and which mouth — two separate choices.
 *
 * They used to be one setting, and Speechify is why they are not: it speaks and
 * does not listen, so a family that wants its voice still needs the browser or
 * Whisper doing the hearing. The split is the better arrangement regardless —
 * the browser listens for nothing and keeps the audio on the device, and the
 * voice is the half worth paying for.
 */
import { logAssistant } from '../log';
import { loadSettings, type AssistantSettings } from '../settings';
import { createBrowserStt, createBrowserTts } from './browser';
import { createRemoteStt, createRemoteTts } from './remote';
import { createServerStt, createServerTts } from './server';
import { createSpeechifyTts } from './speechify';
import type { SpeechToText, TextToSpeech } from './types';

const SILENT: TextToSpeech = {
  id: 'off',
  available: () => false,
  voices: async () => [],
  speak: async () => {},
  stop: () => {},
};

const DEAF: SpeechToText = {
  id: 'off',
  live: false,
  available: () => false,
  start: async (_lang, h) => {
    h.onError('assistant.voice.failed');
    return { stop() {}, cancel() {} };
  },
};

export function createStt(s: AssistantSettings = loadSettings()): SpeechToText {
  /*
   * With the app's own server listening, the browser still goes first when it
   * can — it is instant and shows the words as they come — and the server takes
   * over where it cannot: Firefox, and the phones whose browsers have no
   * recogniser. Choosing a hosted listener on the admin screen means "always the
   * server", whose key it now is.
   */
  if (s.server?.listen && s.listenWith !== 'off') {
    const browser = createBrowserStt();
    if (s.listenWith === 'browser' && browser.available()) return browser;
    return createServerStt(s.listenModel);
  }
  switch (s.listenWith) {
    case 'browser':
      return createBrowserStt();
    case 'openai':
      return createRemoteStt({ apiKey: s.listenKey, baseUrl: s.listenUrl, flavour: 'openai' });
    case 'proxy':
      return createRemoteStt({ apiKey: s.listenKey, baseUrl: s.listenUrl, flavour: 'proxy' });
    case 'server':
      // Chosen while a server was there, opened somewhere there is none.
      return createBrowserStt();
    case 'off':
    default:
      return DEAF;
  }
}

/** The chosen voice, before the fallback below is wrapped round it. */
function rawTts(s: AssistantSettings): TextToSpeech {
  // The server's voice, whenever it has one and speaking is not switched off.
  if (s.server?.speak && s.speakWith !== 'off') return createServerTts(s.server, createBrowserTts(), s.speakModel);
  switch (s.speakWith) {
    case 'browser':
      return createBrowserTts();
    case 'speechify':
      return createSpeechifyTts({ apiKey: s.speakKey, baseUrl: s.speakUrl, voiceId: s.voiceName });
    case 'openai':
      return createRemoteTts({ apiKey: s.speakKey, baseUrl: s.speakUrl, flavour: 'openai' });
    case 'proxy':
      return createRemoteTts({ apiKey: s.speakKey, baseUrl: s.speakUrl, flavour: 'proxy' });
    case 'server':
      return createBrowserTts();
    case 'off':
    default:
      return SILENT;
  }
}

/**
 * The chosen voice, with the browser's own behind it.
 *
 * Reading the answer out is not a garnish any more — it is how this assistant is
 * meant to be used, and somebody who never looks at the screen gets nothing at
 * all if the voice fails. Hosted voices fail for ordinary reasons: an expired
 * key, a rate limit, a flat spot in the signal, or a browser refusing the call
 * on CORS grounds, which is a real possibility for any of these services and is
 * not something a family can debug.
 *
 * So a failed call is not the end of the sentence. It falls through to the
 * browser's own voice — plainer, always there, free — and the person hears their
 * answer. It is not worth interrupting somebody's question for.
 *
 * **But it is written down.** This used to go to the console and nowhere else,
 * which meant a family could pay for Speechify, hear the browser's flat voice
 * instead, and have no way whatsoever to find out — the setting said Speechify,
 * the screen said Speechify, and something else was talking. A fallback nobody
 * can see is indistinguishable from a setting that does not work. Every one of
 * these now lands in the log (see `log.ts`) with the reason attached.
 */
function withFallback(primary: TextToSpeech, fallback: TextToSpeech): TextToSpeech {
  if (primary.id === fallback.id || primary.id === 'off') return primary;
  let used: TextToSpeech = primary;
  return {
    id: primary.id,
    available: () => primary.available() || fallback.available(),
    voices: () => primary.voices(),
    async speak(text, lang, voiceName) {
      if (primary.available()) {
        try {
          used = primary;
          await primary.speak(text, lang, voiceName);
          logAssistant('voice', 'ok', `${primary.id} spoke ${text.length} characters`, voiceName || undefined);
          return;
        } catch (e) {
          logAssistant('voice', 'fail', `${primary.id} failed — falling back to the browser's own voice`, e);
        }
      } else {
        logAssistant('voice', 'warn', `${primary.id} is not usable here — falling back to the browser's own voice`);
      }
      used = fallback;
      // The chosen voice's name means nothing to the browser's own list.
      try {
        await fallback.speak(text, lang, undefined);
        logAssistant('voice', 'warn', "the browser's own voice spoke instead");
      } catch (e) {
        logAssistant('voice', 'fail', 'nothing spoke at all — the browser refused too', e);
        throw e;
      }
    },
    stop() {
      used.stop();
      primary.stop();
      fallback.stop();
    },
  };
}

export function createTts(s: AssistantSettings = loadSettings()): TextToSpeech {
  return withFallback(rawTts(s), createBrowserTts());
}

/**
 * The voice list for one service, for the settings screen, without building a
 * speaker to keep.
 *
 * An empty list is not an error — the browser's voices arrive late, a custom
 * endpoint may publish none — so it is logged rather than shown. But it is the
 * first sign that a key is wrong, and it used to be invisible: the dropdown
 * simply became a text box and nobody knew why.
 */
export function voicesFor(s: AssistantSettings): Promise<Array<{ id: string; name: string }>> {
  return rawTts(s)
    .voices()
    .then((v) => {
      if ((s.speakWith !== 'browser' && s.speakWith !== 'off') || s.server?.speak) {
        logAssistant('voice', v.length ? 'ok' : 'warn', `${s.server?.speak ? 'the server' : s.speakWith} offered ${v.length} voices`);
      }
      return v;
    });
}

/**
 * Say one line with the chosen service, with nothing behind it.
 *
 * The Test button on the admin screen, and the opposite of `createTts`: no
 * fallback, because the whole question being asked is whether *this* service
 * works. A fallback here would answer "yes" in the browser's voice every time.
 */
export async function speakTest(s: AssistantSettings, text: string, lang: 'en' | 'he'): Promise<void> {
  const tts = rawTts(s);
  if (!tts.available()) throw new Error(`${tts.id} is not usable in this browser`);
  await tts.speak(text, lang, s.voiceName || undefined);
}

export type { ListenOptions, Listening, SpeechToText, TextToSpeech } from './types';
